/**
 * Orchestra AI Platform - API Toolbox Wrapper
 * 
 * Wrapper component for the API Toolbox that works with tab-based navigation
 */

import { useState, useEffect } from 'react';
import { APIToolbox } from '../Toolbox/APIToolbox';
import { discoverAllCapabilities } from '../../services/a2aDiscoveryService';
import PropTypes from 'prop-types';
import toast from 'react-hot-toast';

/**
 * Orchestra Toolbox Component
 * @param {Object} props
 * @param {Object} props.currentUser - Current user object
 * @param {Function} props.onBack - Back navigation handler
 * @param {Function} props.onTabChange - Tab change handler
 */
export function OrchestraToolbox({ currentUser, onBack, onTabChange }) {
  const [apis, setAPIs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Discover APIs on mount
  useEffect(() => {
    loadAPIs();
  }, []);

  /**
   * Load APIs from all A2A servers
   */
  const loadAPIs = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('[Orchestra Toolbox] Discovering APIs from all A2A servers...');
      const discoveredAPIs = await discoverAllCapabilities();
      
      if (discoveredAPIs.length === 0) {
        setError('No APIs discovered. Please check server connections.');
        toast.error('No APIs found. Check server connections.');
      } else {
        setAPIs(discoveredAPIs);
        toast.success(`Discovered ${discoveredAPIs.length} APIs`);
        console.log(`[Orchestra Toolbox] Loaded ${discoveredAPIs.length} APIs`);
      }
    } catch (err) {
      console.error('[Orchestra Toolbox] Failed to load APIs:', err);
      setError(err.message || 'Failed to discover APIs');
      toast.error('Failed to discover APIs: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle API selection
   * @param {Object} api - Selected API
   * @param {string} action - Action type (test, info, etc.)
   */
  const handleAPISelect = (api, action) => {
    console.log('API selected:', api, 'Action:', action);
    
    if (action === 'test') {
      // Open in workflow builder with this API
      onTabChange('workflow-builder', { selectedAPI: api });
    } else if (action === 'info') {
      // Show API details
      toast(`${api.name}: ${api.description}`, { icon: 'ℹ️' });
    }
  };

  return (
    <div style={{ padding: '20px', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937', marginBottom: '8px' }}>
            🧰 Orchestra AI Platform - API 工具箱
          </h1>
          {loading && (
            <p style={{ fontSize: '14px', color: '#6b7280' }}>Discovering APIs from A2A servers...</p>
          )}
          {!loading && apis.length > 0 && (
            <p style={{ fontSize: '14px', color: '#6b7280' }}>
              {apis.length} APIs available from 4 A2A servers
            </p>
          )}
          {error && (
            <p style={{ fontSize: '14px', color: '#ef4444' }}>Error: {error}</p>
          )}
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={loadAPIs}
            disabled={loading}
            style={{
              padding: '8px 16px',
              backgroundColor: loading ? '#9ca3af' : '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '14px'
            }}
          >
            {loading ? 'Loading...' : '🔄 Refresh'}
          </button>
          <button
            onClick={onBack}
            style={{
              padding: '8px 16px',
              backgroundColor: '#6b7280',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            ← 返回
          </button>
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto' }}>
        {loading ? (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100%',
            flexDirection: 'column',
            gap: '16px'
          }}>
            <div style={{ fontSize: '48px' }}>⏳</div>
            <p style={{ color: '#6b7280' }}>Discovering APIs from A2A servers...</p>
          </div>
        ) : error ? (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100%',
            flexDirection: 'column',
            gap: '16px'
          }}>
            <div style={{ fontSize: '48px' }}>⚠️</div>
            <p style={{ color: '#ef4444', marginBottom: '16px' }}>{error}</p>
            <button
              onClick={loadAPIs}
              style={{
                padding: '10px 20px',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              Try Again
            </button>
          </div>
        ) : (
          <APIToolbox
            apis={apis}
            onAPISelect={handleAPISelect}
          />
        )}
      </div>
    </div>
  );
}

OrchestraToolbox.propTypes = {
  currentUser: PropTypes.object,
  onBack: PropTypes.func.isRequired,
  onTabChange: PropTypes.func.isRequired,
};

